import { useState, useEffect } from 'react';
import { DemographicsInputs } from '@/components/shared/DemographicsInputs';
import { patchDemographics, emptyDemographics, type DemographicsDraft } from '@/lib/firebase/queries/patientDemographics';
import { useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '@/lib/firebase/config';
import { sendOTP, verifyOTP, resendOTP, isPhoneAvailable, cleanupOTP } from '@/lib/services/otp-verification';
import { nameTranslationService } from '@/lib/services/nameTranslationService';
import { createUser } from '@/lib/firebase/auth';
import { reservePhone } from '@/lib/firebase/queries/phone-management';
import { signInWithGoogle } from '@/lib/firebase/auth/google';
import { useLoginMethods } from '@/hooks/use-login-methods';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OTPInput } from '@/components/ui/otp-input';
import { toast } from 'sonner';
import { Loader2, Phone, Mail } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type Method = 'phone' | 'email' | 'google';
type PhoneStep = 'phone' | 'otp' | 'name';

export function SignupForm() {
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const isAr = dir === 'rtl';
  const lang: 'en' | 'ar' = isAr ? 'ar' : 'en';
  const { settings } = useLoginMethods();

  const enabled: Method[] = [
    ...(settings.phoneOtp ? (['phone'] as Method[]) : []),
    ...(settings.emailPassword ? (['email'] as Method[]) : []),
    ...(settings.googleLogin ? (['google'] as Method[]) : []),
  ];

  const [method, setMethod] = useState<Method | null>(null);
  useEffect(() => {
    if (enabled.length === 1 && enabled[0] !== 'google') setMethod(enabled[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.phoneOtp, settings.emailPassword, settings.googleLogin]);

  const [fullName, setFullName] = useState('');
  const [demographics, setDemographics] = useState<DemographicsDraft>(emptyDemographics);
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<PhoneStep>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailPhone, setEmailPhone] = useState('');

  const splitName = (): { first: string; last: string } | null => {
    const words = fullName.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return null;
    return { first: words[0], last: words.slice(1).join(' ') };
  };

  const translate = async (first: string, last: string) => {
    try {
      return await nameTranslationService.autoTranslate(first, last);
    } catch {
      return isAr
        ? { firstNameAr: first, lastNameAr: last, firstNameEn: first, lastNameEn: last }
        : { firstNameEn: first, lastNameEn: last, firstNameAr: first, lastNameAr: last };
    }
  };

  const handleSendOtp = async () => {
    if (!/^05\d{8}$/.test(phone)) { toast.error(t('auth.phoneInvalidFormat') || 'Phone must be 05XXXXXXXX'); return; }
    setLoading(true);
    try {
      const avail = await isPhoneAvailable(phone);
      if (!avail.available) { toast.error(t('otp.errors.phoneAlreadyRegistered') || 'This phone is already registered. Please log in.'); return; }
      const res = await sendOTP(phone, lang);
      if (!res.success) { toast.error(res.error || t('auth.otpSendFailed') || 'Failed to send code'); return; }
      setCode(''); setStep('otp');
    } finally { setLoading(false); }
  };
  const handleVerify = async () => {
    setLoading(true);
    try {
      const res = await verifyOTP(phone, code);
      if (!res.success) { toast.error(res.error || t('auth.otpInvalid') || 'Invalid code'); return; }
      setStep('name');
    } finally { setLoading(false); }
  };
  const handleResend = async () => {
    const res = await resendOTP(phone, lang);
    if (res.success) toast.success(t('auth.otpResent') || 'Code resent'); else toast.error(res.error || '');
  };
  const handlePhoneRegister = async () => {
    const nm = splitName();
    if (!nm) { toast.error(t('auth.nameMinWords') || 'Please enter your full name (at least two words)'); return; }
    setLoading(true);
    try {
      const names = await translate(nm.first, nm.last);
      const register = httpsCallable<
        { phone: string; firstNameEn: string; lastNameEn: string; firstNameAr: string; lastNameAr: string; gender?: string; dateOfBirth?: string },
        { token: string; userData: any }
      >(functions, 'registerCustomerByPhone');
      const { data } = await register({
        phone, ...names,
        gender: demographics.gender || undefined,
        dateOfBirth: demographics.dateOfBirth || undefined,
      });
      await signInWithCustomToken(auth, data.token);
      await cleanupOTP(phone);
      toast.success(t('auth.accountCreated') || 'Account created');
      navigate('/dashboard/customer');
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('phone-already-registered')) toast.error(t('otp.errors.phoneAlreadyRegistered') || 'This phone is already registered. Please log in.');
      else if (msg.includes('otp-not-verified')) { toast.error(t('auth.otpInvalid') || 'Please verify your phone again.'); setStep('phone'); }
      else toast.error(t('auth.createAccountError') || 'Failed to create account');
    } finally { setLoading(false); }
  };

  const handleEmailRegister = async () => {
    const nm = splitName();
    if (!nm) { toast.error(t('auth.nameMinWords') || 'Please enter your full name (at least two words)'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error(t('auth.invalidEmail') || 'Invalid email'); return; }
    if (password.length < 6) { toast.error(t('auth.passwordMinLength') || 'Password must be at least 6 characters'); return; }
    if (!/^05\d{8}$/.test(emailPhone)) { toast.error(t('auth.phoneInvalidFormat') || 'Phone must be 05XXXXXXXX'); return; }
    setLoading(true);
    try {
      const avail = await isPhoneAvailable(emailPhone);
      if (!avail.available) { toast.error(t('otp.errors.phoneAlreadyRegistered') || 'This phone is already registered. Please log in.'); return; }
      const names = await translate(nm.first, nm.last);
      const res = await createUser({ ...names, email, password, phone: emailPhone, role: 'customer' });
      if (res.user) await reservePhone(emailPhone, res.user.uid);
      if (res.user) await patchDemographics(res.user.uid, demographics).catch((err) => console.warn('demographics not saved', err));
      toast.success(t('auth.accountCreated') || 'Account created');
      navigate('/login');
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('email-already-in-use') || msg.toLowerCase().includes('email')) toast.error(t('auth.emailInUse') || 'Email already in use');
      else toast.error(t('auth.createAccountError') || 'Failed to create account');
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      const res = await signInWithGoogle();
      if (res.user) navigate('/dashboard/customer');
    } catch (e: any) {
      toast.error(e?.message || t('auth.createAccountError') || 'Failed');
    } finally { setLoading(false); }
  };

  const showBack = method && enabled.length > 1;

  return (
    <div className="space-y-6 bg-card p-8 rounded-lg shadow-lg" dir={dir}>
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold">{t('auth.createAccount') || 'Create account'}</h1>
      </div>

      {showBack && (
        <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => { setMethod(null); setStep('phone'); }}>
          {t('auth.chooseAnotherMethod') || 'Choose another method'}
        </button>
      )}

      {!method && (
        <div className="space-y-3">
          {enabled.includes('phone') && (
            <Button variant="outline" className="w-full" disabled={loading} onClick={() => setMethod('phone')}>
              <Phone className="me-2 h-4 w-4" />{t('auth.methodPhone') || 'Sign up with phone'}
            </Button>
          )}
          {enabled.includes('email') && (
            <Button variant="outline" className="w-full" disabled={loading} onClick={() => setMethod('email')}>
              <Mail className="me-2 h-4 w-4" />{t('auth.methodEmail') || 'Sign up with email'}
            </Button>
          )}
          {enabled.includes('google') && (
            <Button variant="outline" className="w-full" disabled={loading} onClick={handleGoogle}>
              {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
              {t('auth.methodGoogle') || 'Sign up with Google'}
            </Button>
          )}
          {enabled.length === 0 && (
            <p className="text-center text-muted-foreground">{t('auth.noMethods') || 'Signup is currently unavailable.'}</p>
          )}
        </div>
      )}

      {method === 'phone' && step === 'phone' && (
        <div className="space-y-4">
          <Input type="tel" inputMode="numeric" placeholder="0512345678" dir="ltr" value={phone} disabled={loading}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
          <Button className="w-full" disabled={loading} onClick={handleSendOtp}>
            {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}{t('auth.sendCode') || 'Send code'}
          </Button>
        </div>
      )}
      {method === 'phone' && step === 'otp' && (
        <div className="space-y-4">
          <div className="flex justify-center"><OTPInput value={code} onChange={setCode} length={4} onComplete={handleVerify} /></div>
          <Button className="w-full" disabled={loading || code.length < 4} onClick={handleVerify}>
            {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}{t('auth.verify') || 'Verify'}
          </Button>
          <div className="flex justify-between text-sm">
            <button type="button" className="text-muted-foreground hover:underline" onClick={() => setStep('phone')}>{t('common.back') || 'Back'}</button>
            <button type="button" className="text-primary hover:underline" onClick={handleResend}>{t('auth.resendCode') || 'Resend code'}</button>
          </div>
        </div>
      )}
      {method === 'phone' && step === 'name' && (
        <div className="space-y-4">
          <Input placeholder={t('auth.fullName') || 'Full name'} dir={isAr ? 'rtl' : 'ltr'} value={fullName} disabled={loading}
            onChange={(e) => setFullName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handlePhoneRegister(); }} />
          <DemographicsInputs value={demographics} onChange={setDemographics} disabled={loading} />
          <Button className="w-full" disabled={loading} onClick={handlePhoneRegister}>
            {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}{t('auth.createAccount') || 'Create account'}
          </Button>
        </div>
      )}

      {method === 'email' && (
        <div className="space-y-4">
          <Input placeholder={t('auth.fullName') || 'Full name'} dir={isAr ? 'rtl' : 'ltr'} value={fullName} disabled={loading} onChange={(e) => setFullName(e.target.value)} />
          <Input type="email" placeholder={t('auth.email') || 'Email'} dir="ltr" value={email} disabled={loading} onChange={(e) => setEmail(e.target.value)} />
          <Input type="tel" inputMode="numeric" placeholder="0512345678" dir="ltr" value={emailPhone} disabled={loading} onChange={(e) => setEmailPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
          <Input type="password" placeholder={t('auth.password') || 'Password'} dir="ltr" value={password} disabled={loading} onChange={(e) => setPassword(e.target.value)} />
          <DemographicsInputs value={demographics} onChange={setDemographics} disabled={loading} />
          <Button className="w-full" disabled={loading} onClick={handleEmailRegister}>
            {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}{t('auth.createAccount') || 'Create account'}
          </Button>
        </div>
      )}

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          {t('auth.alreadyHaveAccount') || 'Already have an account?'}{' '}
          <Link to="/login" className="text-primary hover:underline">{t('auth.login') || 'Log in'}</Link>
        </p>
      </div>
    </div>
  );
}
